import * as THREE from "three";
import { EditorScene } from "./scene";
import { store } from "./state";
import { isAddKind, newId, type Opening } from "./types";
import { addAperture, clampAperture, ensureApertures, offsetAlongWall, setWallFromEnds, snapWorld, wallEnds } from "./wall";

const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
const hit = new THREE.Vector3();

type DragKind = "move" | "endA" | "endB" | "aperture";

export function bindInteraction(editor: EditorScene): void {
  const el = editor.renderer.domElement;
  let dragging = false;
  let dragKind: DragKind = "move";
  let dragApertureId: string | null = null;
  let dragOffset = new THREE.Vector3();

  const setPointer = (ev: PointerEvent) => {
    const r = el.getBoundingClientRect();
    pointer.x = ((ev.clientX - r.left) / r.width) * 2 - 1;
    pointer.y = -((ev.clientY - r.top) / r.height) * 2 + 1;
  };

  el.addEventListener("pointerdown", (ev) => {
    if (ev.button !== 0) return;
    setPointer(ev);
    raycaster.setFromCamera(pointer, editor.camera);
    if (store.viewOnly) {
      const hits = raycaster.intersectObjects(editor.pickables, true);
      const id = hits.map((h) => findItemId(h.object)).find(Boolean) ?? null;
      if (id) {
        store.select(id);
        editor.updateSelection();
        store.notify();
      }
      return;
    }

    if (store.placeMode === "wall") {
      if (!raycaster.ray.intersectPlane(ground, hit)) return;
      const snapped = snapWallPoint(hit.x, hit.z, !store.wallStart);
      if (!store.wallStart) {
        store.wallStart = snapped;
        editor.setWallPreview(store.wallStart, store.wallStart);
        store.notify();
        return;
      }
      store.addWall(store.wallStart.x, store.wallStart.z, snapped.x, snapped.z);
      editor.setWallPreview(null, null);
      editor.rebuild();
      store.notify();
      return;
    }

    if (isAddKind(store.placeMode)) {
      if (raycaster.ray.intersectPlane(ground, hit)) {
        store.addAt(store.placeMode, hit.x, hit.z);
        editor.rebuild();
        store.notify();
      }
      return;
    }

    const hits = raycaster.intersectObjects(editor.pickables, true);

    if (store.placeMode === "door" || store.placeMode === "window") {
      const wallHit = hits.find(isOpeningTarget);
      if (wallHit) {
        addOpening(wallHit);
        store.placeMode = "select";
        editor.rebuild();
        store.notify();
      }
      return;
    }

    const openingHit = hits.find((h) => h.object.userData.openingId || h.object.userData.apertureId);
    if (openingHit && !openingHit.object.userData.handle) {
      const id = findItemId(openingHit.object);
      if (id) {
        store.select(id);
        store.apertureId =
          (typeof openingHit.object.userData.openingId === "string" && openingHit.object.userData.openingId) ||
          (typeof openingHit.object.userData.apertureId === "string" && openingHit.object.userData.apertureId) ||
          null;
        editor.updateSelection();
        store.notify();
      }
      return;
    }

    const handleHit = hits.find((h) => h.object.userData.handle && h.object.visible);
    if (handleHit) {
      const id = findItemId(handleHit.object);
      if (id) {
        store.select(id);
        dragKind = handleHit.object.userData.handle as DragKind;
        dragApertureId = typeof handleHit.object.userData.apertureId === "string" ? handleHit.object.userData.apertureId : null;
        if (dragKind === "aperture") store.apertureId = dragApertureId;
        store.beginEdit();
        dragging = true;
        editor.controls.enabled = false;
        el.setPointerCapture(ev.pointerId);
        store.notify();
      }
      return;
    }

    if (hits.length) {
      const id = findItemId(hits[0].object);
      if (id) {
        store.select(id, ev.shiftKey);
        const face = hits[0].object.userData.wall;
        if (!ev.shiftKey && store.selected()?.kind === "room" && typeof face === "number") {
          store.wallFace = face as 0 | 1 | 2 | 3;
        }
        editor.updateSelection();
        store.notify();
        const item = store.selected();
        if (item && !ev.shiftKey && raycaster.ray.intersectPlane(ground, hit)) {
          dragOffset.set(item.x - hit.x, 0, item.z - hit.z);
          dragKind = "move";
          dragging = true;
          store.beginEdit();
          editor.controls.enabled = false;
          el.setPointerCapture(ev.pointerId);
        }
      }
    }
  });

  el.addEventListener("pointermove", (ev) => {
    setPointer(ev);
    raycaster.setFromCamera(pointer, editor.camera);
    if (store.placeMode === "wall" && store.wallStart && raycaster.ray.intersectPlane(ground, hit)) {
      editor.setWallPreview(store.wallStart, snapWallPoint(hit.x, hit.z, false));
      return;
    }
    if (!dragging || !store.selectedId) return;
    if (!raycaster.ray.intersectPlane(ground, hit)) return;
    const item = store.selected();
    if (!item) return;

    if (dragKind === "move") {
      const nx = hit.x + dragOffset.x;
      const nz = hit.z + dragOffset.z;
      const primary = store.selected();
      if (!primary) return;
      const dx = nx - primary.x;
      const dz = nz - primary.z;
      const ids = store.selectedIds.length ? store.selectedIds : [store.selectedId];
      for (const id of ids) {
        if (!id) continue;
        const it = store.project.items.find((i) => i.id === id);
        if (!it) continue;
        store.moveItem(id, it.x + dx, it.z + dz);
      }
      editor.syncTransforms();
      store.notify();
      return;
    }

    if (item.kind !== "wall") return;
    if (dragKind === "endA") {
      const live = wallEnds(item);
      const p = snapWallEnd(hit.x, hit.z, item.id);
      setWallFromEnds(item, p.x, p.z, live.bx, live.bz);
      editor.rebuild();
      store.notify();
      return;
    }
    if (dragKind === "endB") {
      const live = wallEnds(item);
      const p = snapWallEnd(hit.x, hit.z, item.id);
      setWallFromEnds(item, live.ax, live.az, p.x, p.z);
      editor.rebuild();
      store.notify();
      return;
    }
    if (dragKind === "aperture") {
      const aps = ensureApertures(item);
      const ap = aps.find((a) => a.id === dragApertureId) ?? aps[0];
      if (!ap) return;
      ap.offset = offsetAlongWall(item, hit.x, hit.z) - ap.width / 2;
      clampAperture(item, ap);
      store.apertureId = ap.id;
      editor.rebuild();
      store.notify();
    }
  });

  const endDrag = (ev: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    dragKind = "move";
    dragApertureId = null;
    store.endEdit();
    editor.controls.enabled = true;
    try {
      el.releasePointerCapture(ev.pointerId);
    } catch {
      /* already released */
    }
  };
  el.addEventListener("pointerup", endDrag);
  el.addEventListener("pointercancel", endDrag);

  el.addEventListener("dblclick", (ev) => {
    if (ev.button !== 0) return;
    if (store.placeMode !== "select") return;
    setPointer(ev);
    raycaster.setFromCamera(pointer, editor.camera);
    const hits = raycaster.intersectObjects(editor.pickables, true);
    if (hits.length) {
      const p = hits[0].point;
      editor.setFocus(p.x, p.y, p.z);
      return;
    }
    if (raycaster.ray.intersectPlane(ground, hit)) editor.setFocus(hit.x, 0.4, hit.z);
  });

  window.addEventListener("keydown", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
    if (store.viewOnly) {
      if (ev.key === "f" || ev.key === "F") {
        const item = store.selected();
        if (item) editor.focusItem(item);
      }
      if (ev.key === "c" || ev.key === "C") editor.resetFocus();
      return;
    }

    if ((ev.ctrlKey || ev.metaKey) && (ev.key === "z" || ev.key === "Z")) {
      ev.preventDefault();
      const ok = ev.shiftKey ? store.redo() : store.undo();
      if (ok) editor.rebuild();
      return;
    }
    if ((ev.ctrlKey || ev.metaKey) && (ev.key === "y" || ev.key === "Y")) {
      ev.preventDefault();
      if (store.redo()) editor.rebuild();
      return;
    }
    if ((ev.ctrlKey || ev.metaKey) && (ev.key === "d" || ev.key === "D")) {
      ev.preventDefault();
      if (!store.selectedId) return;
      store.duplicateSelected();
      editor.rebuild();
      store.notify();
      return;
    }
    if (ev.key === "f" || ev.key === "F") {
      const item = store.selected();
      if (!item) return;
      editor.focusItem(item);
      return;
    }
    if (ev.key === "c" || ev.key === "C") {
      editor.resetFocus();
      return;
    }
    if (ev.key === "r" || ev.key === "R") {
      if (!store.selectedId) return;
      store.rotateItem(store.selectedId, Math.PI / 2);
      editor.syncTransforms();
      store.notify();
    }
    if (ev.key === "Delete" || ev.key === "Backspace") {
      ev.preventDefault();
      if (store.removeSelectedOpening()) {
        editor.rebuild();
        store.notify();
        return;
      }
      store.removeSelected();
      editor.rebuild();
      store.notify();
    }
    if (ev.key === "Escape") {
      store.placeMode = "select";
      store.wallStart = null;
      store.apertureId = null;
      editor.setWallPreview(null, null);
      editor.updateSelection();
      store.notify();
    }
  });
}

function snapWallPoint(x: number, z: number, isStart: boolean): { x: number; z: number } {
  const ids = store.selectedIds;
  const prefer = ids.length === 2 ? [isStart ? ids[0] : ids[1]] : undefined;
  return snapWorld(x, z, store.project.items, undefined, prefer);
}

function snapWallEnd(x: number, z: number, wallId: string): { x: number; z: number } {
  const prefer = store.selectedIds.filter((id) => id !== wallId);
  return snapWorld(x, z, store.project.items, wallId, prefer.length ? prefer : undefined);
}

function findItemId(obj: THREE.Object3D): string | null {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (typeof o.userData.itemId === "string") return o.userData.itemId;
    o = o.parent;
  }
  return null;
}

function isOpeningTarget(h: THREE.Intersection): boolean {
  const id = findItemId(h.object);
  const item = store.project.items.find((i) => i.id === id);
  if (!item) return false;
  if (item.kind === "wall") return true;
  return h.object.userData.wall !== undefined && (item.kind === "house" || item.kind === "room");
}

function addOpening(hitObj: THREE.Intersection): void {
  const id = findItemId(hitObj.object);
  const item = store.project.items.find((i) => i.id === id);
  if (!item) return;
  const kind = store.placeMode === "door" ? "door" : "window";

  if (item.kind === "wall") {
    const width = kind === "door" ? 0.8 : 1.2;
    const ap = addAperture(item, kind, offsetAlongWall(item, hitObj.point.x, hitObj.point.z) - width / 2);
    store.select(item.id);
    store.apertureId = ap.id;
    return;
  }

  if (item.kind !== "house" && item.kind !== "room") return;
  const wall = hitObj.object.userData.wall as 0 | 1 | 2 | 3;
  item.openings ??= [];
  const parent = hitObj.object.parent;
  const local = parent ? parent.worldToLocal(hitObj.point.clone()) : hitObj.point;
  let t = 0.5;
  if (wall === 0 || wall === 2) t = (local.x + item.width / 2) / item.width;
  else t = (local.z + item.depth / 2) / item.depth;
  t = Math.min(0.88, Math.max(0.12, t));
  const op: Opening = {
    id: newId(),
    kind,
    wall,
    t,
    height: kind === "door" ? 0 : 1.05,
  };
  item.openings.push(op);
  store.select(item.id);
}
